import { Spinner } from '@chakra-ui/react';
import { ApiPaymentRoutes } from '@components/modules/apiPayment';
import {
  authMagicLinkPath,
  AuthRouter,
  emailVerificationPath,
  loginPath,
  verifyMagicLinkPath,
} from '@components/modules/auth';
import { BusinessCreationRoutes } from '@components/modules/businessCreation';
import { businessCreationPath } from '@components/modules/businessCreation/urls';
import { CustomerPaymentRoutes } from '@components/modules/customerPayment';
import { dashboardPath, DashboardRoutes } from '@components/modules/dashboard';
import { DisputeRoutes } from '@components/modules/dispute';
import { DocumentRoutes } from '@components/modules/Documents';
import { ConnectionErrorPage, Error404Page } from '@components/modules/error';
import { HelpRoutes } from '@components/modules/help';
import { LandingPage } from '@components/modules/landing';
import { NotificationRoutes } from '@components/modules/notification';
import { PaymentRoutes } from '@components/modules/payment';
import { ProfileRoute } from '@components/modules/profile';
import { RecurringPaymentRoutes } from '@components/modules/recurringPayment';
import { RefundRoutes } from '@components/modules/refund';
import { ReportRoutes } from '@components/modules/report';
import { SystemRoutes } from '@components/modules/system';
import { TransferRoutes } from '@components/modules/transfer';
import { AppLayout, AuthLayout } from '@components/templates';
import { WindowTitle } from '@components/ui/atoms/WindowTitle';
import { useGoogleAnalytics } from '@shared/analytics';
import { useAuthContext } from '@shared/auth';
import errorTracker from '@shared/errorTracking/errorTracker';
import { SentryRoutes } from '@shared/router';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, useSearchParams } from 'react-router-dom';

const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      background: 'white',
      zIndex: 2999,
    }}
  >
    <Spinner color="purple.400" size="lg" />
  </div>
);

export const Routes: React.FCWithChildren = ({ children }) => {
  useGoogleAnalytics();

  const { authenticated, authenticating, hasMerchantAccount } = useAuthContext();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect-url');

  // Show loading spinner at top level during authentication
  if (authenticating) {
    return (
      <>
        <WindowTitle title="Bhutan Payments" />
        <LoadingSpinner />
      </>
    );
  }

  return (
    <>
      <WindowTitle title="Bhutan Payments" />

      <ErrorBoundary
        onError={errorTracker.captureException}
        fallbackRender={() => <ConnectionErrorPage />}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <SentryRoutes>
            {/* Public customer payment routes - no authentication required */}
            {CustomerPaymentRoutes}
            {ApiPaymentRoutes}
            {DocumentRoutes}
            {RecurringPaymentRoutes}
            <Route path="/" element={<LandingPage />} />
            {/* Authenticated routes */}
            {/* hasMerchantAccount === false only: while it is still undefined
                (resolving), the full app route table below must stay mounted so a
                transient render can never drop a valid URL out of the table. */}
            {authenticated && hasMerchantAccount === false ? (
              <Route
                path="*"
                element={
                  <AuthLayout>
                    <SentryRoutes>
                      {BusinessCreationRoutes}
                      <Route
                        path="*"
                        element={<Navigate to={businessCreationPath} replace />}
                      />
                    </SentryRoutes>
                  </AuthLayout>
                }
              />
            ) : authenticated ? (
              <Route
                path="*"
                element={
                  <AppLayout>
                    <SentryRoutes>
                      <Route
                        path={loginPath}
                        element={<Navigate to={redirectUrl || dashboardPath} replace />}
                      />
                      <Route
                        path={businessCreationPath}
                        element={<Navigate to={dashboardPath} replace />}
                      />
                      <Route
                        path={verifyMagicLinkPath}
                        element={<Navigate to={dashboardPath} replace />}
                      />
                      <Route
                        path={authMagicLinkPath}
                        element={<Navigate to={dashboardPath} replace />}
                      />
                      <Route
                        path={emailVerificationPath}
                        element={<Navigate to={dashboardPath} replace />}
                      />
                      {DashboardRoutes}
                      {PaymentRoutes}
                      {ProfileRoute}
                      {RefundRoutes}
                      {DisputeRoutes}
                      {TransferRoutes}
                      {ReportRoutes}
                      {SystemRoutes}
                      {NotificationRoutes}
                      {HelpRoutes}
                      {children ?? null}
                      <Route path="*" element={<Error404Page source="catch-all" />} />
                    </SentryRoutes>
                  </AppLayout>
                }
              />
            ) : (
              <Route path="*" element={<AuthRouter />} />
            )}
          </SentryRoutes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
